import { useCallback, useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { createServerFn } from "@tanstack/react-start";
import { requireCoachAuth } from "~/lib/coach-middleware";
import * as z from "zod";
import { ImageInput } from "~/components/file-input";
import { ErrorMessage, FormField } from "~/components/ui/form-field";
import {
  FormCheckboxField,
  FormInputField,
} from "~/components/form/form-fields";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { Checkbox } from "~/components/ui/checkbox";
import { Controller } from "react-hook-form";
import { coachesCcos } from "../../../../../../drizzle/schema";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { ProfileEditHeader } from "../../../../../components/profile-edit-header";
import { cn } from "~/lib/cn";
import { and, eq } from "drizzle-orm";
import {
  createCertification,
  deleteCertification,
  updateCertification,
} from "~/lib/server/coach";

export const QualificationsSchema = z.object({
  id: z.string(),
  certifications: z.optional(
    z.array(
      z.object({
        id: z.optional(z.string()),
        _delete: z.boolean(),
        order: z.optional(z.number()),
        title: z.string(),
        issuer: z.string(),
        imageSrc: z.nullable(z.string()),
        url: z.nullable(z.string()),
        tempId: z.optional(z.string()),
      }),
    ),
  ),
  ccoIds: z.array(z.string()),
});

type FormData = z.infer<typeof QualificationsSchema>;

export const getCoachQualifications = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const coach = context.coach;

    const expandedCoach = await db.query.coaches.findFirst({
      where: (coaches, { eq }) => eq(coaches.id, coach.id),
      with: {
        certifications: {
          orderBy: (certifications, { asc }) => asc(certifications.order),
        },
        ccos: true,
      },
    });

    if (!expandedCoach) {
      throw new Error("Could not find additional required coach info");
    }

    const ccoIds = expandedCoach.ccos.map((cc) => cc.ccoId);

    return {
      ...expandedCoach,
      certifications: expandedCoach.certifications.map((c) => ({
        ...c,
        _delete: false,
        tempId: `existing-${c.id}`,
      })),
      ccoIds,
    };
  });

export const getCcos = createServerFn({ method: "GET" }).handler(async () => {
  const { getGroupedCCOs } = await import("~/lib/ccos");
  return await getGroupedCCOs({ data: { all: true } });
});

export const saveQualifications = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(QualificationsSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const coach = context.coach;

    // Process certifications in two phases: first deletions, then updates/creates
    const certificationsToProcess = data.certifications ?? [];

    // Phase 1: Handle deletions first
    const toDelete = certificationsToProcess.filter((c) => c._delete && c.id);
    for (const cert of toDelete) {
      await deleteCertification(cert.id!);
    }

    // Phase 2: Handle updates and creates for non-deleted items
    const toKeep = certificationsToProcess.filter((c) => !c._delete);
    for (const [newOrder, c] of toKeep.entries()) {
      if (!c.id) {
        // New certification
        const { id: _id, _delete, tempId: _tempId, ...certificationData } = c;
        await createCertification({
          ...certificationData,
          coachId: coach.id,
          verified: false,
          order: newOrder,
        });
      } else {
        // Existing certification - update with new order
        const { id: _id, _delete, tempId: _tempId, ...certificationData } = c;
        await updateCertification(c.id, {
          ...certificationData,
          order: newOrder,
        });
      }
    }

    const currentCcos = await db.query.coachesCcos.findMany({
      where: eq(coachesCcos.coachId, coach.id),
    });

    const currentCcoIds = new Set(currentCcos.map((cc) => cc.ccoId));
    const newCcoIds = new Set(data.ccoIds || []);

    for (const currentCcoId of currentCcoIds) {
      if (!newCcoIds.has(currentCcoId)) {
        await db
          .delete(coachesCcos)
          .where(
            and(
              eq(coachesCcos.coachId, coach.id),
              eq(coachesCcos.ccoId, currentCcoId),
            ),
          );
      }
    }

    for (const newCcoId of newCcoIds) {
      if (!currentCcoIds.has(newCcoId)) {
        await db.insert(coachesCcos).values({
          coachId: coach.id,
          ccoId: newCcoId,
        });
      }
    }

    return {
      status: "success",
      message: "Profile saved",
    };
  });

export const Route = createFileRoute(
  "/coach/_backend/edit-profile/qualifications/",
)({
  head: () =>
    createNoIndexMeta(
      "Qualifications – Dream Coach Match",
      "Manage your coaching qualifications and certifications",
    ),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["coach", "qualifications"],
      queryFn: () => getCoachQualifications(),
    });
    await context.queryClient.ensureQueryData({
      queryKey: ["ccos"],
      queryFn: () => getCcos(),
    });
    return {};
  },
  component: QualificationsPage,
});

function QualificationsPage() {
  const queryClient = useQueryClient();

  const { data: coach } = useQuery({
    queryKey: ["coach", "qualifications"],
    queryFn: () => getCoachQualifications(),
  });

  const { data: allCcos } = useQuery({
    queryKey: ["ccos"],
    queryFn: () => getCcos(),
  });

  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    getValues,
    control,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(QualificationsSchema),
    defaultValues: coach || {
      id: "",
      certifications: [],
      ccoIds: [],
    },
  });

  const { errors, isDirty } = formState;
  const certifications = watch("certifications");

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => saveQualifications({ data }),
    onSuccess: async () => {
      toast.success("Qualifications saved successfully");

      // Invalidate broader coach cache to update dependent data
      await queryClient.invalidateQueries({ queryKey: ["coach"] });

      // Refetch fresh data and reset form to get new IDs and remove deleted items
      const freshData = await queryClient.fetchQuery({
        queryKey: ["coach", "qualifications"],
        queryFn: () => getCoachQualifications(),
      });

      // Reset form with fresh data from server
      reset(freshData);
    },
    onError: (_error: unknown) => {
      toast.error("Failed to save qualifications");
    },
  });

  const { showExitAlert, onDiscard } = useUnsavedChangesAlert(() => isDirty);

  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && 0 < Object.keys(errors).length,
  );

  const [ccoTypeDisplayName, setCcoTypeDisplayName] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const loadCcoDisplayNames = async () => {
      try {
        const { CCO_TYPE_DISPLAY_NAME } = await import("~/lib/ccos");
        setCcoTypeDisplayName(CCO_TYPE_DISPLAY_NAME);
      } catch (error) {
        console.error("Failed to load CCO display names:", error);
      }
    };

    loadCcoDisplayNames();
  }, []);

  if (!coach || !allCcos) {
    return <div>Loading...</div>;
  }

  const groupedCcoOptions = Object.entries(allCcos).map(
    ([type, subgroups]) => ({
      type,
      displayName: ccoTypeDisplayName[type] ?? type,
      subgroups: Object.entries(subgroups).map(([subcategory, ccos]) => ({
        subcategory,
        ccos,
      })),
    }),
  );

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const addCertification = useCallback(() => {
    const currentCerts = getValues("certifications") || [];
    const newCertification = {
      _delete: false,
      id: undefined,
      title: "",
      issuer: "",
      imageSrc: null,
      url: null,
      order: currentCerts.length,
      tempId: `temp-${Date.now()}-${Math.random()}`,
    };
    setValue("certifications", [...currentCerts, newCertification], {
      shouldDirty: true,
    });
  }, [getValues, setValue]);

  const moveCertification = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentCerts = getValues("certifications") || [];
      if (0 > toIndex || toIndex >= currentCerts.length) {
        return;
      }

      const updatedCerts = [...currentCerts];
      const [movedItem] = updatedCerts.splice(fromIndex, 1);
      updatedCerts.splice(toIndex, 0, movedItem);

      // Update order values
      updatedCerts.forEach((cert, idx) => {
        cert.order = idx;
      });

      setValue("certifications", updatedCerts, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getValues, setValue],
  );

  return (
    <>
      <UnsavedChangesAlert show={showExitAlert} onDiscard={onDiscard} />
      <PristineInvalidAlert {...pristineAlert} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <ProfileEditHeader
          title="Qualifications"
          description="Add your certifications and experience."
          form={formState}
          username={coach.username}
        />

        <input {...register("id")} type="hidden" value={coach.id} />

        <Card className={errors.ccoIds ? "border-red-500" : ""}>
          <CardHeader>
            <CardTitle>Coach Certifying Organizations</CardTitle>
            <p className="text-gray-500">
              Select the coaching organizations you're certified with or
              affiliated to. This will add you to their directory pages on our
              platform.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6">
              {groupedCcoOptions.map((group) => (
                <div key={group.type} className="flex flex-col gap-2">
                  <div className="text-brand font-semibold">
                    {group.displayName}
                  </div>
                  {group.subgroups.map((sub) => (
                    <div key={sub.subcategory}>
                      <div className="mb-2 text-sm text-gray-600">
                        {"Uncategorized" !== sub.subcategory ? (
                          sub.subcategory
                        ) : (
                          <span className="italic">Other</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <Controller
                          control={control}
                          name="ccoIds"
                          render={({ field }) => {
                            const values = field.value || [];
                            return (
                              <>
                                {sub.ccos.map((cco) => (
                                  <Checkbox
                                    key={cco.id}
                                    value={cco.id}
                                    checked={values.includes(cco.id)}
                                    onCheckedChange={(checked) => {
                                      const newValues = checked
                                        ? [...values, cco.id]
                                        : values.filter((id) => id !== cco.id);
                                      field.onChange(newValues);
                                    }}
                                  >
                                    {cco.name}
                                  </Checkbox>
                                ))}
                              </>
                            );
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {errors.ccoIds && <ErrorMessage error={errors.ccoIds.message} />}
          </CardContent>
        </Card>

        <Card className={errors.certifications ? "border-red-500" : ""}>
          <CardHeader>
            <CardTitle>Certifications</CardTitle>
            <p className="text-gray-500">
              List any certifications you have that are relevant to your
              coaching practice.
            </p>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              className="self-start mb-4"
              onClick={addCertification}
            >
              <Plus className="mr-2 h-4 w-4" /> Add Certification
            </Button>

            <div className="space-y-4">
              {(certifications || []).map((cert, index) => {
                const certKey = cert.id || cert.tempId || `cert-${index}`;
                return (
                  <div
                    key={certKey}
                    className={cn("group w-full space-y-4 p-4 border rounded", {
                      "opacity-50": cert._delete,
                    })}
                  >
                    {cert.id && (
                      <input
                        {...register(`certifications.${index}.id`)}
                        type="hidden"
                      />
                    )}

                    <div className="flex w-full items-center justify-between gap-2">
                      <FormCheckboxField
                        control={control}
                        name={`certifications.${index}._delete`}
                        label="Delete"
                      />
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveCertification(index, index - 1)}
                          disabled={0 === index}
                          title="Move Up"
                        >
                          <ArrowUp className="size-[1em]" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveCertification(index, index + 1)}
                          disabled={index === (certifications?.length || 0) - 1}
                          title="Move Down"
                        >
                          <ArrowDown className="size-[1em]" />
                        </Button>
                      </div>
                    </div>

                    <FormInputField
                      control={control}
                      name={`certifications.${index}.title`}
                      label="Title"
                      required
                      helpText="The name of the certification"
                    />

                    <FormInputField
                      control={control}
                      name={`certifications.${index}.issuer`}
                      label="Issuer"
                      required
                      helpText="The organization that issued the certification"
                    />

                    <FormField
                      label="Image"
                      helpText="(32×32) The badge (round logo) representing the issuer or certification"
                      error={errors.certifications?.[index]?.imageSrc?.message}
                    >
                      <ImageInput
                        {...register(`certifications.${index}.imageSrc`)}
                        value={cert.imageSrc}
                        type="url"
                        error={!!errors.certifications?.[index]?.imageSrc}
                      />
                    </FormField>

                    <FormInputField
                      control={control}
                      name={`certifications.${index}.url`}
                      label="URL"
                      helpText="Link to the organization or program by which you were qualified"
                    />
                  </div>
                );
              })}
            </div>
            {errors.certifications && (
              <ErrorMessage error={errors.certifications.message} />
            )}
          </CardContent>
        </Card>
      </form>
    </>
  );
}
